import { useState } from "react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { ChevronDown, ChevronRight, PlusSquare } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { SectionPaletteControls } from "./section-palette-controls";
import { SectionPaletteGroup } from "./section-palette-group";
import { useSectionPalette } from "../hooks/use-section-palette";

type SectionPaletteProps = {
  analysisId: string;
  stepsCount: number;
};

export function SectionPalette({
  analysisId,
  stepsCount,
}: SectionPaletteProps) {
  const [open, setOpen] = useState(false);
  const {
    search,
    setSearch,
    showGlobal,
    setShowGlobal,
    grouped,
    isLoading,
    hasResults,
    openTemplates,
    setOpenTemplates,
    selectedStepKey,
    setSelectedStepKey,
    insertTop,
    insertBottom,
    insertAt,
    insertDisabled,
    isInserting,
    clearSelection,
    stepsCount: resolvedStepsCount,
    blankSectionStepKey,
    selectedStepName,
  } = useSectionPalette({ analysisId, stepsCount });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section className="space-y-4">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-muted/50"
          >
            {open ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )}
            <span className="min-w-0">
              <span className="block text-base font-medium leading-tight">
                Section Palette
              </span>
              <span className="block text-sm text-muted-foreground">
                Insert steps from your existing templates
              </span>
            </span>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-4">
          <SectionPaletteControls
            search={search}
            onSearchChange={setSearch}
            showGlobal={showGlobal}
            onToggleGlobal={setShowGlobal}
            insertDisabled={insertDisabled}
            isInserting={isInserting}
            stepsCount={resolvedStepsCount}
            onInsertTop={insertTop}
            onInsertBottom={insertBottom}
            onInsertAt={insertAt}
            onClearSelection={clearSelection}
            hasSelection={Boolean(selectedStepKey)}
            selectedStepName={selectedStepName}
          />

          {isLoading ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Loading palette…</CardTitle>
                <CardDescription>
                  Fetching steps from your templates.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : null}

          {!isLoading && !hasResults ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">No matching steps</CardTitle>
                <CardDescription>
                  Try adjusting your search or filters.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : null}

          {!isLoading ? (
            <div className="grid items-start gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <button
                type="button"
                onClick={() => setSelectedStepKey(blankSectionStepKey)}
                className="self-start text-left"
              >
                <Card
                  className={`h-16 gap-0 overflow-hidden py-0 transition-colors hover:bg-muted/60 ${
                    selectedStepKey === blankSectionStepKey
                      ? "border-primary/45 bg-primary/5"
                      : ""
                  }`}
                >
                  <CardHeader className="h-full px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex size-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <PlusSquare className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-sm leading-tight">
                          Create Blank Section
                        </CardTitle>
                        <CardDescription className="mt-0.5 text-xs">
                          Start from an empty section
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </button>

              {grouped.map((group) => {
                const isOpen = openTemplates[group.template.id] ?? false;

                return (
                  <SectionPaletteGroup
                    key={group.template.id}
                    template={group.template}
                    steps={group.steps}
                    isOpen={isOpen}
                    onOpenChange={(next) =>
                      setOpenTemplates((prev) => ({
                        ...prev,
                        [group.template.id]: next,
                      }))
                    }
                    selectedStepKey={selectedStepKey}
                    onSelectStep={setSelectedStepKey}
                  />
                );
              })}
            </div>
          ) : null}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}
